import { ArrowDownRight, ArrowUpRight } from 'lucide-react';
import { Card, CardLabel } from './ui/card';
import { cn } from '@/lib/cn';

interface StatCardProps {
  label: string;
  value: number | string | null | undefined;
  delta?: number;
  deltaLabel?: string;
  loading?: boolean;
  error?: string | null;
  hint?: string;
  className?: string;
}

export function StatCard({
  label,
  value,
  delta,
  deltaLabel,
  loading,
  error,
  hint,
  className,
}: StatCardProps): JSX.Element {
  const hasDelta = typeof delta === 'number';
  const trend = !hasDelta ? 'flat' : delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat';

  return (
    <Card className={cn('flex flex-col gap-3', className)}>
      <CardLabel>{label}</CardLabel>
      {loading ? (
        <div className="h-10 w-24 animate-pulse rounded-md bg-surface-divider/40" />
      ) : error ? (
        <div className="text-sm text-state-danger">{error}</div>
      ) : (
        <div className="text-[32px] font-extrabold leading-none tabular-nums">
          {value === null || value === undefined
            ? '—'
            : typeof value === 'number'
              ? value.toLocaleString()
              : value}
        </div>
      )}

      {!loading && !error && deltaLabel && (
        <div
          className={cn(
            'flex items-center gap-1 text-xs font-medium',
            trend === 'up' && 'text-brand-cyan',
            trend === 'down' && 'text-state-danger',
            trend === 'flat' && 'text-fg-subtle',
          )}
        >
          {trend === 'up' && <ArrowUpRight size={12} strokeWidth={2} />}
          {trend === 'down' && <ArrowDownRight size={12} strokeWidth={2} />}
          <span>{deltaLabel}</span>
        </div>
      )}

      {hint && !loading && !error && (
        <div className="text-xs text-fg-subtle">{hint}</div>
      )}
    </Card>
  );
}
